import {
  ref,
  Ref,
  reactive,
  computed,
} from 'vue'
import i18next from 'i18next'
import apiDoctorCaseServices from 'www/vue_components/common/DoctorCases/api/apiDoctorCaseServices'
import { isNetworkDisconnect } from 'www/utils'
import getUserLanguage from 'src/utils/getUserLanguage'
import { DIALOG_PROPS } from 'www/vue_components/common/DoctorCases/constants'
import { ApiDoctorCaseServicesResponseGet } from 'www/vue_components/common/DoctorCases/api/apiDoctorCaseServices.types'

const userLanguage = getUserLanguage()

type Options = {
  alertDialogProps: Ref<typeof DIALOG_PROPS[keyof typeof DIALOG_PROPS]>
  alertDialogActive: Ref<boolean>
}

export default function useCaseServices({ alertDialogProps, alertDialogActive }: Options) {
  const isLoading = ref(false)
  const isVisibleModal = ref(false)
  const caseServicesMap = new Map<number, ApiDoctorCaseServicesResponseGet>()
  const caseServicesData = ref<ApiDoctorCaseServicesResponseGet>({
    is_doctor_work_at_lpu: false,
    results: [],
  })
  const lastCaseId = ref<null | number>(null)
  const dialogProps = computed(() => ({
    default: {
      ...DIALOG_PROPS.default,
      title: i18next.t(DIALOG_PROPS.default.title, { ns: userLanguage }),
      contentText: i18next.t(DIALOG_PROPS.default.contentText, { ns: userLanguage }),
      confirmText: i18next.t(DIALOG_PROPS.default.confirmText, { ns: userLanguage }),
    },
    disconnect: {
      ...DIALOG_PROPS.disconnect,
      title: i18next.t(DIALOG_PROPS.disconnect.title, { ns: userLanguage }),
      contentText: i18next.t(DIALOG_PROPS.disconnect.contentText, { ns: userLanguage }),
      confirmText: i18next.t(DIALOG_PROPS.disconnect.confirmText, { ns: userLanguage }),
    },
  }))

  function closeModal() {
    isVisibleModal.value = false
  }

  async function openModal(caseId: number) {
    isVisibleModal.value = true
    lastCaseId.value = caseId

    if (caseServicesMap.has(caseId)) {
      caseServicesData.value = caseServicesMap.get(caseId) as ApiDoctorCaseServicesResponseGet
      return
    }

    if (isLoading.value) {
      return
    }

    isLoading.value = true

    try {
      const response = await apiDoctorCaseServices.get({ caseId })

      caseServicesMap.set(caseId, response)

      if (lastCaseId.value === caseId) {
        caseServicesData.value = response
      }
    } catch (error: any) {
      if (lastCaseId.value !== caseId) {
        return
      }

      const { response, message } = error || {}
      const { status } = response || {}

      alertDialogActive.value = true
      isVisibleModal.value = false

      if (isNetworkDisconnect({ status, message })) {
        alertDialogProps.value = dialogProps.value.disconnect
        return
      }

      alertDialogProps.value = dialogProps.value.default
    } finally {
      if (lastCaseId.value === caseId) {
        // eslint-disable-next-line require-atomic-updates
        isLoading.value = false
      }
    }
  }

  return reactive({
    caseServicesData,
    isLoading,
    isVisibleModal,
    closeModal,
    openModal,
  })
}
